import { AccessoryType, Accessory, LedType } from '@/api/AccessoryApi';
import {
	AttributeTarget,
	Attribute,
	AttributeViewType,
} from '@/api/AttributeApi';
import { AttributeValue } from '@/api/AttributeValueApi';
import { Product, ProductyAccessory } from '@/api/ProductApi';
import {
	ProductTypeVariation,
	ProductTypeVariationAccessory,
	AccessoryMountingSide,
} from '@/api/ProductTypeVariationApi';
import { ProjectRenderer } from '@/renderer/ProjectRenderer';
import { SerializedRenderer, UserDataObjectType } from '@/renderer/types';
import { CartState } from '@/store/slices/cartSlice';
import { getContent } from '@/utils/contentUtils';
import {
	PostType,
	CreatorPost,
	AvailableAttributeValues,
	AvailableAccessories,
	CreatorState,
	CreatorAccessory,
	SerializedCreator,
	SerializedCreatorPost,
	SerializedCreatorAccessory,
	SerializedCreatorEnding,
	CreatorStep,
	CreatorEndings,
	NotificationID,
} from '@/types/creator';
import { EntityStatus, UUID } from '@/types/types';
import { omit } from 'lodash';
import { MathUtils } from 'three';
import { WritableDraft } from '@reduxjs/toolkit';
import { notifications } from '@mantine/notifications';
import { ContentContext } from '@/providers/ContentProvider';
import APP_CONFIG from '@/configs/appConfig';

const renderer = ProjectRenderer.getInstance();

export function getEmptyCreatorPost(position: PostType, posY = 0): CreatorPost {
	return {
		id: crypto.randomUUID(),
		posY,
		type: position,
		linkedCreatorAccessoryId: null,
		selectedValues: {},
		customValues: {},
		isAutoAdded: false,
		isAdjustable: true,
	};
}

export function getAvailableAttributeValues(
	productTypeVariation: WritableDraft<ProductTypeVariation>,
	allAttributes: WritableDraft<Attribute[]>
) {
	const availableAttributeValues: AvailableAttributeValues = {
		MAIN: [],
		POSTS: [],
		ENDINGS: [],
		STEP_ACCESSORIES: [],
	};

	for (const attributeValue of productTypeVariation.attributeValues) {
		const attribute = allAttributes.find(
			(attr) => attr.id === attributeValue.attributeId
		);
		if (!attribute) continue;

		if (attribute.target === AttributeTarget.ACCESSORIES) continue;

		const available = availableAttributeValues[attribute.target];
		const attr = available.find((attr) => attr.id === attribute.id);

		if (!attr)
			available.push({
				...attribute,
				attributeValues: [attributeValue],
			});
		else attr.attributeValues.push(attributeValue);
	}

	let key: keyof AvailableAttributeValues;
	for (key in availableAttributeValues) {
		const attributes = availableAttributeValues[key];
		attributes.sort((a, b) => a.listOrder - b.listOrder);

		for (const attribute of attributes) {
			attribute.attributeValues.sort((a, b) => a.listOrder - b.listOrder);
		}
	}

	return availableAttributeValues;
}

export function getAvailableAccessories(
	productTypeVariation: WritableDraft<ProductTypeVariation>
) {
	const availableAccessories: AvailableAccessories = {
		[AccessoryType.ACCESS_CONTROL]: [],
		[AccessoryType.DRIVER]: [],
		[AccessoryType.LED]: [],
		[AccessoryType.CABLE_GLAND]: [],
	};

	for (const variationAccessory of productTypeVariation.variationAccessories) {
		if (variationAccessory.accessory.status === EntityStatus.INACTIVE) continue;

		availableAccessories[variationAccessory.accessory.type].push(
			variationAccessory
		);
	}

	return availableAccessories;
}

export function getInitialSelectedValues(
	productTypeVariation: WritableDraft<ProductTypeVariation>,
	allAttributes: WritableDraft<Attribute[]>,
	defaultAttributeValues: Record<UUID, UUID | null>
) {
	const selectedValues: CreatorState['selectedValues'] = {};

	for (const attributeValue of productTypeVariation.attributeValues) {
		const attribute = allAttributes.find(
			(attr) => attr.id === attributeValue.attributeId
		);
		if (!attribute) continue;

		if (selectedValues[attribute.id]) continue;

		if (defaultAttributeValues[attribute.id]) {
			const value = productTypeVariation.attributeValues.find(
				(av) => av.id === defaultAttributeValues[attribute.id]
			);
			if (!value) continue;

			selectedValues[attribute.id] = value;
			continue;
		}

		selectedValues[attribute.id] = null;
	}

	return selectedValues;
}

export function getRequiredMiddlePostsQuantity(
	length: number,
	autoMiddlePostThreshold: number[]
) {
	for (let i = autoMiddlePostThreshold.length - 1; i >= 0; i--) {
		if (autoMiddlePostThreshold[i] <= length) return i + 1;
	}

	return 0;
}

export function detectMaxSpacingExceed(
	creatorPosts: WritableDraft<CreatorPost[]>,
	maxSpacing: number
) {
	const middlePosts = creatorPosts
		.filter((post) => post.type === PostType.MIDDLE)
		.toSorted((a, b) => b.posY - a.posY);

	if (!Number.isFinite(maxSpacing) || middlePosts.length < 2) {
		notifications.hide(NotificationID.MIDDLE_POSTS_MAX_SPACING_EXCEEDED);
		return;
	}

	const sorted = [...middlePosts].sort((a, b) => a.posY - b.posY);

	for (let i = 1; i < sorted.length; i++) {
		if (sorted[i].posY - sorted[i - 1].posY > maxSpacing) {
			notifications.show({
				id: NotificationID.MIDDLE_POSTS_MAX_SPACING_EXCEEDED,
				color: 'red',
				title: getContent('notifications.middlePostsMaxSpacingTitle'),
				message: getContent('notifications.middlePostsMaxSpacingMessage', {
					vars: { maxSpacing: maxSpacing.toString() },
				}),
				autoClose: false,
			});
			return;
		}
	}

	notifications.hide(NotificationID.MIDDLE_POSTS_MAX_SPACING_EXCEEDED);
}

export function getAccessoryTypeSlug(type: AccessoryType) {
	switch (type) {
		case AccessoryType.ACCESS_CONTROL:
			return 'accessories.accessControl';
		case AccessoryType.DRIVER:
			return 'accessories.driver';
		case AccessoryType.LED:
			return 'accessories.led';
		case AccessoryType.CABLE_GLAND:
			return 'accessories.cableGland';
	}
}

export function getAccessoryAvailableAttributeValues(
	accessory: Accessory,
	allAttributes: WritableDraft<Attribute[]>
) {
	const availableAttributes: CreatorAccessory['availableAttributes'] = [];

	for (const attributeValue of accessory.attributeValues) {
		const attribute = allAttributes.find(
			(attr) => attr.id === attributeValue.attributeId
		);
		if (!attribute) continue;

		const existingAttribute = availableAttributes.find(
			(attr) => attr.id === attribute.id
		);

		if (!existingAttribute) {
			availableAttributes.push({
				...attribute,
				attributeValues: [attributeValue],
			});
		} else {
			existingAttribute.attributeValues.push(attributeValue);
		}
	}

	return availableAttributes;
}

type SerializedSelectedValues = Record<UUID, UUID | null>;

export function deserializeSelectedValues(
	serializedSelectedValues: SerializedSelectedValues,
	attributeValues: AttributeValue[]
) {
	return Object.entries(serializedSelectedValues).reduce<
		Record<UUID, AttributeValue | null>
	>((acc, [attributeId, valueId]) => {
		if (!valueId) return acc;

		const attributeValue = attributeValues.find((av) => av.id === valueId);
		if (!attributeValue) return acc;

		acc[attributeId as UUID] = attributeValue;
		return acc;
	}, {});
}

export function deserializeCreatorFromSerialized(
	serializedCreator: SerializedCreator,
	productTypeVariation: ProductTypeVariation,
	allAttributes: WritableDraft<Attribute[]>
) {
	const variationAttributeValues = productTypeVariation.attributeValues;

	const creatorPosts = serializedCreator.creatorPosts.map((serializedPost) => ({
		...serializedPost,
		selectedValues: deserializeSelectedValues(
			serializedPost.selectedValues,
			variationAttributeValues
		),
	}));

	const creatorAccessories = serializedCreator.creatorAccessories.map(
		(serializedAccessory) => {
			const variationAccessory = productTypeVariation.variationAccessories.find(
				(va) => va.id === serializedAccessory.variationAccessoryId
			);
			if (!variationAccessory) throw new Error('MISSING_VARIATION_ACCESSORY');

			return {
				...serializedAccessory,
				variationAccessory,
				selectedValues: deserializeSelectedValues(
					serializedAccessory.selectedValues,
					variationAccessory.accessory.attributeValues
				),
				availableAttributes: getAccessoryAvailableAttributeValues(
					variationAccessory.accessory,
					allAttributes
				),
			};
		}
	);

	const creatorEndings = Object.entries(
		serializedCreator.creatorEndings
	).reduce<CreatorEndings>((acc, [key, value]) => {
		const endingType = key as keyof CreatorEndings;

		acc[endingType] = {
			selectedValues: deserializeSelectedValues(
				value.selectedValues,
				variationAttributeValues
			),
			customValues: value.customValues,
		};

		return acc;
	}, {} as CreatorEndings);

	return {
		selectedValues: deserializeSelectedValues(
			serializedCreator.selectedValues,
			variationAttributeValues
		),
		customValues: serializedCreator.customValues,
		adjustableHeights: serializedCreator.adjustableHeights,
		creatorPosts,
		creatorAccessories,
		creatorEndings,
	};
}

export function serializeCreator(
	state: WritableDraft<CreatorState>,
	exportedRenderer: SerializedRenderer
): SerializedCreator {
	const selectedValues = Object.entries(state.selectedValues).reduce<
		SerializedCreator['selectedValues']
	>((acc, curr) => {
		const [attributeId, selectedValue] = curr;
		acc[attributeId as UUID] = selectedValue?.id || null;
		return acc;
	}, {});

	const creatorPosts = state.creatorPosts.map((creatorPost) => {
		const selectedValues = Object.entries(creatorPost.selectedValues).reduce<
			SerializedCreatorPost['selectedValues']
		>((acc, curr) => {
			const [attributeId, selectedValue] = curr;
			acc[attributeId as UUID] = selectedValue?.id || null;
			return acc;
		}, {});

		return {
			id: creatorPost.id,
			posY: creatorPost.posY,
			type: creatorPost.type,
			linkedCreatorAccessoryId: creatorPost.linkedCreatorAccessoryId,
			selectedValues,
			customValues: creatorPost.customValues,
			isAutoAdded: creatorPost.isAutoAdded,
			isAdjustable: creatorPost.isAdjustable,
		};
	});

	return {
		productTypeId: state.productType!.id,
		creatorPosts,
		adjustableHeights: state.adjustableHeights,
		selectedValues,
		customValues: state.customValues,
		creatorAccessories: state.creatorAccessories.map((creatorAccessory) => {
			const ledTapesData: SerializedCreatorAccessory['ledTapesData'] = [];

			if (
				creatorAccessory.variationAccessory.accessory.ledType ===
				LedType.LED_TAPE
			)
				for (const rendererLedTape of renderer.objectsManager.ledsManager
					.ledTapes) {
					ledTapesData.push({
						posY: Math.round(
							(rendererLedTape.posY +
								exportedRenderer.handleManager.originToYCenterConst) *
								1000
						),
						length: Math.round(rendererLedTape.length * 1000),
					});
				}

			return {
				...omit(creatorAccessory, [
					'availableAttributes',
					'selectedValues',
					'variationAccessory',
				]),
				ledTapesData,
				variationAccessoryId: creatorAccessory.variationAccessory.id,
				selectedValues: Object.entries(creatorAccessory.selectedValues).reduce<
					SerializedCreatorAccessory['selectedValues']
				>((acc, curr) => {
					const [attributeId, selectedValue] = curr;
					acc[attributeId as UUID] = selectedValue?.id || null;
					return acc;
				}, {}),
			};
		}),
		creatorEndings: {
			[UserDataObjectType.UPPER_ENDING_ADJUSTABLE]: {
				...state.creatorEndings[UserDataObjectType.UPPER_ENDING_ADJUSTABLE],
				selectedValues: Object.entries(
					state.creatorEndings[UserDataObjectType.UPPER_ENDING_ADJUSTABLE]
						.selectedValues
				).reduce<SerializedCreatorEnding['selectedValues']>((acc, curr) => {
					const [attributeId, selectedValue] = curr;
					acc[attributeId as UUID] = selectedValue?.id || null;
					return acc;
				}, {}),
			},
			[UserDataObjectType.LOWER_ENDING_ADJUSTABLE]: {
				...state.creatorEndings[UserDataObjectType.LOWER_ENDING_ADJUSTABLE],
				selectedValues: Object.entries(
					state.creatorEndings[UserDataObjectType.LOWER_ENDING_ADJUSTABLE]
						.selectedValues
				).reduce<SerializedCreatorEnding['selectedValues']>((acc, curr) => {
					const [attributeId, selectedValue] = curr;
					acc[attributeId as UUID] = selectedValue?.id || null;
					return acc;
				}, {}),
			},
		},
		options: state.options,
		exportedRenderer,
	};
}

export function generateProductPayload(
	state: WritableDraft<CreatorState>,
	exportedRenderer: SerializedRenderer,
	local = false
): Partial<Product> {
	return {
		name: state.configurationName,
		productTypeVariationId: state.productTypeVariation!.id,
		productTypeVariation: {
			id: state.productTypeVariation!.id,
		} as ProductTypeVariation,
		customValues: state.customValues,
		attributeValues: Object.values(state.selectedValues)
			.filter((value) => value !== null)
			.map((attributeValue) =>
				local
					? attributeValue
					: ({
							id: attributeValue.id,
						} as AttributeValue)
			),
		productAccessories: state.creatorAccessories.map(
			(creatorAccessory) =>
				({
					id: creatorAccessory.id,
					refId: creatorAccessory.refId,
					mountingSide: creatorAccessory.mountingSide,
					customFieldAnswers: creatorAccessory.customFieldAnswers,
					variationAccessory: {
						id: creatorAccessory.variationAccessory.id,
					} as ProductTypeVariationAccessory,
					attributeValues: Object.values(creatorAccessory.selectedValues)
						.filter((value) => value !== null)
						.map(
							(attributeValue) =>
								({
									id: attributeValue.id,
								}) as AttributeValue
						),
				}) as ProductyAccessory
		),
		serializedCreator: serializeCreator(state, exportedRenderer),
	};
}

export function saveProjectToLocal(
	state: WritableDraft<CreatorState>,
	exportedRenderer: SerializedRenderer
) {
	localStorage.setItem(
		APP_CONFIG.STORAGE_KEYS.TEMP_PROJECT,
		JSON.stringify(generateProductPayload(state, exportedRenderer, true))
	);
}

export function getAccessoryMountingSideNameSlug(
	moutingSide: CreatorAccessory['mountingSide']
) {
	switch (moutingSide) {
		case AccessoryMountingSide.FRONT:
			return 'moutingSide.front';
		case AccessoryMountingSide.BACK:
			return 'moutingSide.back';
	}
}

export function loadCartStateFromLocalStorage(): CartState {
	const cartState = localStorage.getItem(APP_CONFIG.STORAGE_KEYS.CART);
	if (!cartState) return { items: [], lastEditedItemId: null };

	try {
		const payload = JSON.parse(cartState);
		return payload;
	} catch (error) {
		console.error('INVALID_CART_DATA', error);
		return { items: [], lastEditedItemId: null };
	}
}

export function checkAttributeValueAvailability(
	attributeValue: AttributeValue,
	checkList: (AttributeValue | null)[]
) {
	if (!attributeValue.relations.length) return true;

	const checkListSet = new Set(
		checkList.filter((value) => !!value).map((value) => value.id)
	);

	return attributeValue.relations.every(
		(relation) => checkListSet.has(relation.objectId) === relation.present
	);
}

export function setSteps(state: WritableDraft<CreatorState>) {
	setAvailableSteps(state);
	setEnabledSteps(state);
}

export function setEnabledSteps(state: WritableDraft<CreatorState>) {
	const isStepDimensionsAnswered = checkIsDimensionsAttributesAnswered(state),
		isColorsAttributesAnswered =
			isStepDimensionsAnswered && checkIsColorsAttributesAnswered(state);

	state.options.enabledSteps = [
		CreatorStep.DIMENSIONS,
		CreatorStep.ACCESSORIES,
		CreatorStep.COLORS,
		CreatorStep.SUMMARY,
	].filter((step) => {
		if (!state.productTypeVariation) return false;

		switch (step) {
			case CreatorStep.DIMENSIONS:
				return true;

			case CreatorStep.ACCESSORIES:
				if (!isStepDimensionsAnswered) return false;
				return true;

			case CreatorStep.COLORS:
				if (!isStepDimensionsAnswered) return false;
				return true;

			case CreatorStep.SUMMARY:
				if (!isColorsAttributesAnswered) return false;
				return true;
			default:
				break;
		}
	});
}

export function setAvailableSteps(state: WritableDraft<CreatorState>) {
	state.options.availableSteps = [
		CreatorStep.DIMENSIONS,
		CreatorStep.ACCESSORIES,
		CreatorStep.COLORS,
		CreatorStep.SUMMARY,
	].filter((step) => {
		switch (step) {
			case CreatorStep.DIMENSIONS:
				return true;
			case CreatorStep.ACCESSORIES:
				const allAccessories = Object.values(state.availableAccessories).flat();
				return allAccessories.length > 0;
			case CreatorStep.COLORS:
				return true;
			case CreatorStep.SUMMARY:
				return true;
			default:
				break;
		}
	});
}

export function formatAnswerForSummary(
	selectedValue: AttributeValue | null,
	attribute: Attribute,
	customAnswers: Record<string, string>,
	getEntityText: ContentContext['getEntityText']
) {
	let answer = '-';
	if (selectedValue)
		answer = getEntityText(selectedValue, 'displayName') || '-';
	if (
		selectedValue?.isCustomAnswer ||
		attribute.viewType === AttributeViewType.TEXTAREA
	)
		answer = customAnswers[attribute.id] || '-';

	return answer;
}

export function checkIsColorsAttributesAnswered(
	state: WritableDraft<CreatorState>
) {
	const colorCreatorAttributes = Object.values(state.availableAttributeValues)
		.flat()
		.filter(
			(attribute) =>
				attribute.viewType === AttributeViewType.COLOR &&
				state.productTypeVariation?.requiredAttributesIds.includes(attribute.id)
		);

	for (const creatorAttribute of colorCreatorAttributes) {
		switch (creatorAttribute.target) {
			case AttributeTarget.HANDLE:
				if (
					!state.selectedValues[creatorAttribute.id] &&
					!state.customValues[creatorAttribute.id]
				)
					return false;
				continue;
			case AttributeTarget.POSTS:
				if (
					state.creatorPosts.some(
						(post) =>
							!post.selectedValues[creatorAttribute.id] &&
							!post.customValues[creatorAttribute.id]
					)
				)
					return false;
				continue;
			case AttributeTarget.ENDINGS:
				for (const type of [
					UserDataObjectType.UPPER_ENDING_ADJUSTABLE,
					UserDataObjectType.LOWER_ENDING_ADJUSTABLE,
				] as const) {
					if (
						type === UserDataObjectType.LOWER_ENDING_ADJUSTABLE &&
						!state.options.endingsConfiguration.lowerEnding
					)
						continue;
					if (
						type === UserDataObjectType.UPPER_ENDING_ADJUSTABLE &&
						!state.options.endingsConfiguration.upperEnding
					)
						continue;

					if (
						!state.creatorEndings[type].selectedValues[creatorAttribute.id] &&
						!state.creatorEndings[type].customValues[creatorAttribute.id]
					)
						return false;
				}
				continue;
		}
	}

	return true;
}

export function checkIsDimensionsAttributesAnswered(
	state: WritableDraft<CreatorState>
) {
	const creatorAttributes = [
		...state.availableAttributeValues[AttributeTarget.HANDLE],
		...state.availableAttributeValues[AttributeTarget.POSTS],
		...state.availableAttributeValues[AttributeTarget.ENDINGS],
	];

	for (const creatorAttribute of creatorAttributes) {
		if (creatorAttribute.viewType === AttributeViewType.COLOR) continue;
		if (
			!state.productTypeVariation?.requiredAttributesIds.includes(
				creatorAttribute.id
			)
		)
			continue;

		switch (creatorAttribute.viewType) {
			case AttributeViewType.SEGMENT:
			case AttributeViewType.SELECT:
			case AttributeViewType.TILES:
				if (!state.selectedValues[creatorAttribute.id]) return false;
				break;
			case AttributeViewType.TEXTAREA:
				if (!state.customValues[creatorAttribute.id]) return false;
				break;
		}
	}

	const isPostsAttributesAnswered = checkIsPostsAttributesAnswered(state);
	const isEndingsAttributesAnswered = checkIsEndingsAttributesAnswered(state);

	return isPostsAttributesAnswered && isEndingsAttributesAnswered;
}

export function checkIsPostsAttributesAnswered(
	state: WritableDraft<CreatorState>
) {
	const creatorAttributes =
		state.availableAttributeValues[AttributeTarget.POSTS];

	for (const creatorAttribute of creatorAttributes) {
		if (creatorAttribute.viewType === AttributeViewType.COLOR) continue;
		if (
			!state.productTypeVariation?.requiredAttributesIds.includes(
				creatorAttribute.id
			)
		)
			continue;

		switch (creatorAttribute.viewType) {
			case AttributeViewType.SEGMENT:
			case AttributeViewType.SELECT:
			case AttributeViewType.TILES:
				if (
					state.creatorPosts.some(
						(post) => !post.selectedValues[creatorAttribute.id]
					)
				)
					return false;
				break;
			case AttributeViewType.TEXTAREA:
				if (
					state.creatorPosts.some(
						(post) => !post.customValues[creatorAttribute.id]
					)
				)
					return false;
				break;
		}
	}

	return true;
}

export function checkIsEndingsAttributesAnswered(
	state: WritableDraft<CreatorState>
) {
	const creatorAttributes =
		state.availableAttributeValues[AttributeTarget.ENDINGS];

	for (const type of [
		UserDataObjectType.UPPER_ENDING_ADJUSTABLE,
		UserDataObjectType.LOWER_ENDING_ADJUSTABLE,
	] as const) {
		if (
			type === UserDataObjectType.LOWER_ENDING_ADJUSTABLE &&
			!state.options.endingsConfiguration.lowerEnding
		)
			continue;
		if (
			type === UserDataObjectType.UPPER_ENDING_ADJUSTABLE &&
			!state.options.endingsConfiguration.upperEnding
		)
			continue;

		for (const creatorAttribute of creatorAttributes) {
			if (creatorAttribute.viewType === AttributeViewType.COLOR) continue;
			if (
				!state.productTypeVariation?.requiredAttributesIds.includes(
					creatorAttribute.id
				)
			)
				continue;
			switch (creatorAttribute.viewType) {
				case AttributeViewType.SEGMENT:
				case AttributeViewType.SELECT:
				case AttributeViewType.TILES:
					if (!state.creatorEndings[type].selectedValues[creatorAttribute.id])
						return false;
					break;
				case AttributeViewType.TEXTAREA:
					if (!state.creatorEndings[type].customValues[creatorAttribute.id])
						return false;
					break;
			}
		}
	}

	return true;
}

export function validateCreatorAccessories(
	creatorAccessories: CreatorAccessory[]
) {
	for (const creatorAccessory of creatorAccessories) {
		for (const attributeId of creatorAccessory.variationAccessory.accessory
			.requiredAttributesIds) {
			if (creatorAccessory.selectedValues[attributeId]) continue;

			notifications.show({
				id: NotificationID.MISSING_ANSWERS,
				color: 'red',
				title: getContent('notifications.missingAnswersTitle'),
				message: getContent('notifications.missingAnswersMessage'),
				autoClose: 6_000,
			});
			return false;
		}

		for (const customField of creatorAccessory.variationAccessory.accessory
			.customFields) {
			if (!customField.required) continue;

			const answer = creatorAccessory.customFieldAnswers[customField.name];
			if (answer) continue;

			notifications.show({
				id: NotificationID.MISSING_ANSWERS,
				color: 'red',
				title: getContent('notifications.missingAnswersTitle'),
				message: getContent('notifications.missingAnswersMessage'),
				autoClose: 6_000,
			});
			return false;
		}
	}
	return true;
}

export function filterProductTypeVariationAccessories(
	variationAccessories: ProductTypeVariationAccessory[],
	state: WritableDraft<CreatorState>
) {
	const { creatorAccessories, productTypeVariation } = state;

	const middlePosts = state.creatorPosts.filter(
			(post) => post.type === PostType.MIDDLE
		),
		maxMiddlePostQuantity = productTypeVariation
			? productTypeVariation.maxMiddlePostQuantity
			: 0,
		freeMiddlePostsQuantity = maxMiddlePostQuantity - middlePosts.length;

	const filteredByLed = variationAccessories.filter((varAcc) => {
		switch (varAcc.accessory.ledType) {
			case LedType.NONE:
				if (!productTypeVariation?.accessoriesNeedsMiddlePost) return true;
				if (!varAcc.accessory.requireMiddlePost) return true;
				return freeMiddlePostsQuantity > 0;
			case LedType.LED_TAPE:
			case LedType.LED_POINT:
				return !creatorAccessories.some(
					(creaAcc) =>
						![LedType.NONE, LedType.LED_PLUG].includes(
							creaAcc.variationAccessory.accessory.ledType
						)
				);
			case LedType.LED_PLUG:
				return !creatorAccessories.some(
					(creaAcc) =>
						creaAcc.variationAccessory.accessory.ledType === LedType.LED_PLUG
				);

			default:
				break;
		}
	});

	return filterVariationAccessoriesByConditions(filteredByLed, state);
}

export function filterVariationAccessoriesByConditions(
	variationAccessories: ProductTypeVariationAccessory[],
	state: WritableDraft<CreatorState>,
	parentAccessoryId?: UUID
) {
	const { productTypeVariation, options } = state;

	return variationAccessories.filter(({ accessory }) => {
		if (!accessory.availabilityConditions && !parentAccessoryId) return true;

		let accessoryCheck =
			!accessory.availabilityConditions.requiredAccessoryIds.length;
		if (parentAccessoryId)
			accessoryCheck =
				accessory.availabilityConditions.requiredAccessoryIds.includes(
					parentAccessoryId
				);

		const upperEndingCheck =
				productTypeVariation?.endingsAdjustable &&
				accessory.availabilityConditions.upperEnding
					? options.endingsConfiguration.upperEnding
					: true,
			lowerEndingCheck =
				productTypeVariation?.endingsAdjustable &&
				accessory.availabilityConditions.lowerEnding
					? options.endingsConfiguration.lowerEnding
					: true;

		return upperEndingCheck && lowerEndingCheck && accessoryCheck;
	});
}

export function checkMiddlePostLimitReached(
	state: WritableDraft<CreatorState>
) {
	const { productTypeVariation, creatorPosts } = state;

	const maxMiddlePostQuantity = productTypeVariation
		? productTypeVariation.maxMiddlePostQuantity
		: 0;
	const middlePosts = creatorPosts.filter(
		(post) => post.type === PostType.MIDDLE
	);

	if (maxMiddlePostQuantity > middlePosts.length)
		notifications.hide(NotificationID.MIDDLE_POSTS_LIMIT_REACHED);
	else
		notifications.show({
			id: NotificationID.MIDDLE_POSTS_LIMIT_REACHED,
			color: 'red',
			title: getContent('notifications.middlePostsLimitTitle'),
			message: getContent('notifications.middlePostsLimitMessage'),
			autoClose: false,
		});
}

export function parseTotalHeightFormula(state: WritableDraft<CreatorState>) {
	const formula = state.productTypeVariation?.totalHeightFormula;
	if (!formula) return 0;

	const { handle, upperEnding, lowerEnding } = state.adjustableHeights;

	const upperEndingHeight = state.options.endingsConfiguration.upperEnding
			? upperEnding
			: 0,
		lowerEndingHeight = state.options.endingsConfiguration.lowerEnding
			? lowerEnding
			: 0;

	const expression = formula
		.replace(/\$UE/g, String(upperEndingHeight))
		.replace(/\$LE/g, String(lowerEndingHeight))
		.replace(/\$H/g, String(handle))
		.replace(/\bsqrt\s*\(/gi, 'Math.sqrt(')
		.replace(/\bsin\s*\(/gi, 'Math.sin(')
		.replace(/\bcos\s*\(/gi, 'Math.cos(')
		.replace(/\bdeg\s*\(/gi, '__degToRad__(');

	const withoutMathFns = expression
		.replace(/Math\.(sqrt|sin|cos)/g, '')
		.replace(/__degToRad__/g, '');
	if (!/^[\d\s+\-*/().]+$/.test(withoutMathFns)) return 0;

	const result = Function(
		'__degToRad__',
		`"use strict"; return (${expression})`
	)(MathUtils.degToRad);

	return Number.isFinite(result) ? result : 0;
}

export function getPostName(
	post: CreatorPost,
	posts: CreatorPost[],
	getContent: ContentContext['getContent']
) {
	switch (post.type) {
		case PostType.UPPER:
			return getContent('postname.upperPost');
		case PostType.LOWER:
			return getContent('postname.lowerPost');
		default:
			const middlePosts = posts.filter((post) => post.type === PostType.MIDDLE);
			const middlePostIdx = middlePosts.findIndex((p) => p.id === post.id);
			return `${getContent('postname.middlePost')} ${middlePostIdx + 1}`;
	}
}

export function getNewCreatorAccessory(
	variationAccessory: ProductTypeVariationAccessory,
	allAttributes: Attribute[],
	parentCreatorAccessoryRefId: UUID | null = null
) {
	const mountingSide =
		variationAccessory.settings.mountingSide === AccessoryMountingSide.BOTH
			? AccessoryMountingSide.FRONT
			: variationAccessory.settings.mountingSide;

	const selectedValues: CreatorAccessory['selectedValues'] = {};

	let attributeId: UUID;
	for (attributeId in variationAccessory.accessory.defaultAttributeValues) {
		const valueId =
			variationAccessory.accessory.defaultAttributeValues[attributeId];
		if (!valueId) continue;

		const attributeValue = variationAccessory.accessory.attributeValues.find(
			(av) => av.id === valueId
		);
		if (!attributeValue) continue;

		selectedValues[attributeId] = attributeValue;
	}

	const creatorAccessory: CreatorAccessory = {
		refId: crypto.randomUUID(),
		posY: 0,
		customFieldAnswers: {},
		customValues: {},
		variationAccessory,
		parentCreatorAccessoryRefId,
		linkedMiddlePostId: null,
		mountingSide,
		availableAttributes: getAccessoryAvailableAttributeValues(
			variationAccessory.accessory,
			allAttributes
		),
		selectedValues,
		options: {
			autoPositioning: true,
		},
	};

	return creatorAccessory;
}
